import { brandAssets } from '@echo/brand';
import mascotLeaveGroup from '@echo/brand/assets/mascot-leave-group.png';
import { useMe, useMyGroup, useRemoveGroupMember } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { EmptyStateArt, EmptyStateFrame, ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed, GroupLoadFailed, GroupLoading } from '@/components/group-load-state';

export const Route = createFileRoute('/_app/groups_/$id_/leave')({ component: LeaveGroupPage });

/**
 * Figma group/options › Leave group: "Stay in group" goes back to Groups and changes nothing;
 * "Leave group" takes the person out of it and returns to Groups, where its card is gone. A
 * leader or facilitator leaves through Remove from group, which keeps the last one in.
 */
function LeaveGroupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const me = useMe();
  const { group, ...groups } = useMyGroup(id);
  const leaving = useRemoveGroupMember(id);
  const backLink = ({ className, children }: { className: string; children: React.ReactNode }) => (
    <Link to="/groups/$id" params={{ id }} className={className}>
      {children}
    </Link>
  );
  if (groups.isPending || me.isPending) {
    return (
      <>
        <ToolbarBack render={backLink} />
        <GroupLoading />
      </>
    );
  }
  if (groups.isError || me.isError) {
    return (
      <>
        <ToolbarBack render={backLink} />
        <GroupLoadFailed onRetry={() => void (groups.isError ? groups.refetch() : me.refetch())} />
      </>
    );
  }
  // Already out of it, or an unknown id from a link.
  if (!group) return <Navigate to="/groups" replace />;
  const personId = me.data?.uuid;
  if (group.manages && personId) {
    return (
      <Navigate
        to="/account/groups/$id/member/$personId/remove"
        params={{ id, personId }}
        replace
      />
    );
  }
  const leave = () => {
    if (!personId || leaving.isPending) return;
    leaving.mutate(personId, {
      onSuccess: () => void navigate({ to: '/groups', replace: true }),
    });
  };
  return (
    <>
      <ToolbarBack render={backLink} />
      <EmptyStateFrame
        art={
          <EmptyStateArt
            src={mascotLeaveGroup}
            alt={t`A cheerful brain character waving beside an open door`}
            width={brandAssets.mascotLeaveGroup.width}
            height={brandAssets.mascotLeaveGroup.height}
          />
        }
        title={<Trans>Leave this group?</Trans>}
        actions={
          <>
            <Button variant="tertiary" onClick={leave} loading={leaving.isPending}>
              {t`Leave group`}
            </Button>
            <Link to="/groups">
              <Button tabIndex={-1}>
                <Trans>Stay in group</Trans>
              </Button>
            </Link>
          </>
        }
      >
        <Trans>If you leave, you'll need a new invitation to join this group again.</Trans>
        {leaving.isError ? <GroupActionFailed /> : null}
      </EmptyStateFrame>
    </>
  );
}
